import React, { useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import { Controller, FormProvider, SubmitHandler } from 'react-hook-form';

import CloseIcon from '@/assets/icons/icon-close-small.svg';
import IconMedia from '@/assets/icons/icon-media.svg';
import { Loader } from '@/components/Loader';
import { WalletInfoAlternate } from '@/components/widgets/WalletInfo';
import useLoanForm from '@/hooks/loans/useLoanForm';
import useWalletAllNFTs from '@/hooks/query/useWalletAllNFTs';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { changeQuickReqLoans, selectLoans, toogleQuickLoanModal } from '@/redux/loans/loansSlice';
import { divideAndSortNft } from '@/utils/divide-sort-loan-request';
import { QUICK_LOAN_LTV } from 'constant';
import type { IDesiredTerms } from 'types/loan';

import ChangableDesiredTerms from '../ChangableDesiredTerms';
import SecondStepContent from './SecondStepContent';

interface QuickLoanModalProps {
  onSubmit: SubmitHandler<
    IDesiredTerms & {
      termUnix?: number;
    }
  >;
}

const QuickLoanModal: React.FC<QuickLoanModalProps> = ({ onSubmit }) => {
  const dispatch = useAppDispatch();
  const methods = useLoanForm({
    ltv: 50,
    principal: 10,
  });
  const modalRef = useRef(null);
  const inputValue = methods.watch('borrowAmount');
  const { data: tokens, isLoading } = useWalletAllNFTs({
    select(data) {
      return data.walletCollection.filter((nft) => nft.contract.isWhiteListed);
    },
    checkWhiteList: true,
    dontIncludePrice: true,
  });

  const nfts = divideAndSortNft(tokens, inputValue);
  const { requestedQuickLoan } = useAppSelector(selectLoans);
  const [activeStep, setActiveStep] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const floorPrice = requestedQuickLoan?.contract?.openSeaMetadata?.floorPrice;

  const borrowAmount = Number(inputValue);

  useEffect(() => {
    if (borrowAmount != null) {
      if (borrowAmount > 0 && borrowAmount <= 120) {
        setActiveStep(2);
        setError(null);
      } else {
        setActiveStep(1);
      }
    } else {
      setActiveStep(1);
      setError(null);
    }
  }, [borrowAmount]);

  function handleClose() {
    dispatch(toogleQuickLoanModal());
    dispatch(changeQuickReqLoans(null));
  }

  useOnClickOutside(modalRef, handleClose);

  return createPortal(
    <FormProvider {...methods}>
      <div className='absolute bg-white dark:bg-jet-black w-full min-h-screen top-0 z-30 flex overflow-y-auto justify-center items-center p-6'>
        <form ref={modalRef} className='bg-white-100 dark:bg-jet-black-100 border-x border-t w-full max-w-[541px]'>
          <header className='p-6 flex justify-between items-center gap-3 border-b'>
            <h2 className='truncate text-xl font-semibold dark:text-white text-coal-250e max-w-[calc(100%-256px)]'>Quick Loan</h2>
            <div className='gap-4 flex items-center'>
              <WalletInfoAlternate className='h-[29px] !bg-white [&_.text]:!text-primary' />
              <button type='button' onClick={handleClose}>
                <CloseIcon className='[&_.line]:fill-primary dark:[&_.line]:fill-primary-blue-100' />
              </button>
            </div>
          </header>

          {activeStep >= 1 && (
            <section className='pt-[17px] border-b px-11 pb-6 flex flex-col gap-3'>
              <a href='/' target='_blank' rel='noopener noreferrer' className='mx-auto w-fit flex justify-center items-center gap-3 px-6 py-3 bg-primary-blue-400/15 pointer-events-none'>
                <strong className='text-primary dark:text-primary-blue-100 text-sm font-medium'>How to use Quick Loans</strong>
                <IconMedia className='size-[18px] [&_path]:fill-primary dark:[&_path]:fill-primary-blue-100 object-contain' />
              </a>
              <h3 className='text-lg/[25px] font-semibold dark:text-white text-coal-250 text-center'>I’d like to borrow</h3>
              <div className='flex flex-col items-center p-5 w-full dark:bg-jet-black'>
                <label className='flex justify-center'>
                  <div className='flex items-center gap-1 h-10'>
                    <div className='flex items-center justify-center'>
                      <Controller
                        name='borrowAmount'
                        control={methods.control}
                        rules={{ min: 0, max: 120 }}
                        render={({ field: { onChange, value } }) => (
                          <input
                            autoFocus
                            type='text'
                            inputMode='decimal'
                            value={value ?? ''}
                            onKeyDown={(e) => {
                              const invalidKeys = ['e', 'E', '+', '-', ','];
                              if (invalidKeys.includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onChange={(e) => {
                              const { value: rawValue } = e.target;
                              dispatch(changeQuickReqLoans(null));
                              if (rawValue === '') {
                                onChange(null);
                                return;
                              }

                              if (rawValue === '.') {
                                onChange('0.');
                                return;
                              }

                              const cleanedValue = rawValue.replace(/^0+(\d)/, '$1');

                              if (cleanedValue.length > 10) return;

                              if (!/^\d*\.?\d*$/.test(cleanedValue)) return;

                              const number = parseFloat(cleanedValue);

                              if (number < 0) {
                                onChange(null);
                              } else if (number > 120) {
                                return;
                              } else {
                                onChange(cleanedValue);
                              }

                              e.target.style.width = '1ch';
                              e.target.style.width = `${e.target.scrollWidth}px`;
                            }}
                            className='text-[29px]/[25px] w-[1ch] font-semibold dark:text-white text-coal-250 text-right !caret-jet-black dark:!caret-white dark:bg-jet-black bg-white peer focus:!text-coal-250 focus:dark:!text-white !bg-transparent'
                            max={120}
                            step={0.01}
                          />
                        )}
                      />
                    </div>
                    <span className='text-[29px]/[25px] font-semibold dark:!text-white !text-coal-250'>WETH</span>
                  </div>
                </label>

                <strong className='dark:text-white text-center text-coal-250 text-xs/[25px]'>Based on {QUICK_LOAN_LTV}% LTV</strong>
                <p className='dark:text-white text-coal-250 text-sm mt-2'>Enter how much you would like to borrow</p>
              </div>
            </section>
          )}

          {!!inputValue && activeStep >= 2 && <section className='pt-[17px] border-b px-[41px] pb-[30px] flex flex-col gap-[11px]'>{isLoading ? <Loader /> : inputValue > 0 && nfts ? <SecondStepContent nfts={nfts.data} description={nfts.description} showAlert={nfts.key !== 'eligibleAssets'} /> : ''}</section>}

          {requestedQuickLoan && nfts && !!inputValue && inputValue > 0 && (
            <section className='pt-[11px] px-[41px] pb-6 border-b'>
              <h3 className='text-lg/[25px] font-semibold dark:text-white text-coal-250 text-center'>Terms</h3>
              <div>
                <ChangableDesiredTerms disableLtv simplified hideExp title={{ simple: '' }} subtitle={'Based on 50% LTV'} className='max-h-max m-0' assetValue={floorPrice || 0} />
              </div>
              <div className='flex justify-center items-center gap-[14px] pt-6'>
                <button type='button' onClick={methods.handleSubmit(onSubmit)} className='w-[93px] h-[41px] bg-primary text-white rounded text-sm font-medium'>
                  Create
                </button>
                <button type='button' onClick={handleClose} className='w-[93px] h-[41px] dark:bg-jet-black bg-white dark:text-white text-coal-250 rounded text-sm font-medium'>
                  Cancel
                </button>
              </div>
            </section>
          )}
        </form>
      </div>
    </FormProvider>,
    document.querySelector('#app')!
  );
};

export default QuickLoanModal;
